import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';

/**
 * Session timeout warning modal
 * Shows countdown before auto-logout
 */
export function SessionTimeoutModal({ isOpen, remainingMinutes, onStayLoggedIn, onLogout }) {
  const [countdown, setCountdown] = useState(remainingMinutes * 60);

  useEffect(() => {
    if (!isOpen) {
      setCountdown(remainingMinutes * 60);
      return;
    }

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, remainingMinutes, onLogout]);

  if (!isOpen) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}
      data-testid="session-timeout-modal"
    >
      <div 
        className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
        style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid rgba(59, 130, 246, 0.2)'
        }}
      >
        {/* Warning Icon */}
        <div className="flex justify-center mb-4">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(251, 191, 36, 0.15)' }}
          >
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
        </div>

        {/* Title */}
        <h2 
          className="text-xl font-semibold text-center mb-2"
          style={{ color: '#1e293b' }}
        >
          Session Timeout Warning
        </h2>

        {/* Description */}
        <p 
          className="text-center mb-4"
          style={{ color: '#64748b' }}
        >
          Your session will expire due to inactivity.
        </p>

        {/* Countdown */}
        <div 
          className="flex items-center justify-center gap-2 py-4 px-6 rounded-xl mb-6"
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
        >
          <Clock className="w-5 h-5 text-red-500" />
          <span 
            className="text-2xl font-mono font-bold"
            style={{ color: '#ef4444' }}
            data-testid="session-countdown"
          >
            {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
          </span>
        </div>

        {/* CJIS Compliance Note */}
        <p 
          className="text-xs text-center mb-6"
          style={{ color: '#94a3b8' }}
        >
          CJIS Security Policy requires automatic logout after 30 minutes of inactivity.
        </p>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onLogout}
            className="flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-200"
            style={{ 
              backgroundColor: 'rgba(100, 116, 139, 0.1)',
              color: '#64748b',
              border: '1px solid rgba(100, 116, 139, 0.2)'
            }}
            data-testid="session-logout-btn"
          >
            Logout Now
          </button>
          <button
            onClick={onStayLoggedIn}
            className="flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-200 hover:opacity-90"
            style={{ 
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: 'white',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
            data-testid="session-stay-btn"
          >
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionTimeoutModal;
